<template>
  <div class="add-item-wrapper">
    <!-- ===== GLOBAL LOADER ===== -->
    <transition name="fade">
      <div v-if="loading" class="global-loader">
        <div class="loader-spinner"></div>
        <p>{{ form.item_id ? "Updating item..." : "Saving item..." }}</p>
      </div>
    </transition>

    <!-- ===== HEADER ===== -->
    <div class="page-header">
      <div class="header-left">
        <h2>{{ form.item_id ? "Edit Item" : "Add New Item" }}</h2>
        <p>Add your product or service details with pricing and tax information.</p>
      </div>
      <div class="header-right">
        <button class="btn-secondary" @click="cancelItem">
          <i class="bx bx-x"></i> Discard
        </button>
        <button class="btn-primary" @click="saveItem" :disabled="loading">
          <i class="bx bx-save"></i>
          {{ loading ? "Saving..." : form.item_id ? "Update Item" : "Save Item" }}
        </button>
      </div>
    </div>

    <!-- ===== MAIN BODY ===== -->
    <div class="page-body">
      <!-- LEFT PANEL -->
      <div class="left-pane fade-in">
        <div class="card">
          <h3 class="section-title">
            <i class="bx bx-package"></i> Item Information
          </h3>
          <p class="section-desc">
            Provide complete details to help track and bill items easily.
          </p>

          <div class="form-grid" style="margin-top: 1rem;">
            <div class="form-group">
              <label>Item Name</label>
              <input
                type="text"
                v-model="form.item_name"
                placeholder="E.g. AI Consultation Service"
              />
            </div>

            <div class="form-group">
              <label>Item Type</label>
              <select v-model="form.item_category">
                <option>Service</option>
                <option>Product</option>
              </select>
            </div>

            <div class="form-group">
              <label>Stock Quantity</label>
              <input
                type="number"
                v-model="form.item_unit"
                min="0"
                placeholder="E.g. 100"
              />
            </div>

            <div class="form-group">
              <label>Unit Price ($)</label>
              <input
                type="number"
                v-model="form.item_price"
                min="0"
                placeholder="E.g. 250"
              />
            </div>
          </div>

          <div class="form-group full">
            <label>Description</label>
            <textarea
              rows="3"
              v-model="form.item_desc"
              placeholder="Briefly describe this item..."
            ></textarea>
          </div>

          <div class="form-group" style="margin-top: 1rem;">
            <label>Status</label>
            <select v-model="form.item_status">
              <option>Active</option>
              <option>Inactive</option>
            </select>
          </div>

          <!-- FOOTER BUTTONS -->
          <div class="form-footer">
            <button class="btn-secondary" @click="cancelItem">
              <i class="bx bx-arrow-back"></i> Cancel
            </button>
            <button class="btn-primary" @click="saveItem" :disabled="loading">
              <i class="bx bx-check"></i>
              {{ form.item_id ? "Update Item" : "Save Item" }}
            </button>
          </div>
        </div>
      </div>

      <!-- RIGHT PANEL (Tax Section) -->
      <div class="right-pane slide-in">
        <div class="card">
          <h3 class="section-title">
            <i class="bx bx-calculator"></i> Item Taxes
          </h3>
          <p class="section-desc">Define one or more taxes that apply to this item.</p>

          <div class="tax-list" style="margin-top: 1rem;">
            <div v-for="(tax, index) in taxes" :key="index" class="tax-box">
              <div class="tax-inputs">
                <input
                  type="text"
                  v-model="tax.name"
                  placeholder="Tax Name (e.g., GST)"
                />
                <select v-model="tax.type">
                  <option>Percentage</option>
                  <option>Fixed</option>
                </select>
                <input
                  type="number"
                  v-model="tax.value"
                  placeholder="Value"
                />
              </div>

              <button
                v-if="index > 0"
                class="btn-remove"
                title="Remove Tax"
                @click="removeTax(index)"
              >
                <i class="bx bx-trash"></i>
              </button>
            </div>

            <button class="btn-outline" @click="addTax">
              <i class="bx bx-plus"></i> Add Another Tax
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>


<script setup>
import { ref, onMounted, nextTick } from "vue";
import { useToast } from "vue-toastification";
import { addItem, getItemById } from "@/apis/itemapis";

const toast = useToast();
const loading = ref(false);

/* ===== FORM DATA ===== */
const form = ref({
  item_id: "",
  item_name: "",
  item_category: "Product",
  item_unit: "",
  item_price: "",
  item_desc: "",
  item_tax: "",
  item_discount: "",
  item_status: "Active",
  item_by: "",
});

/* ===== TAXES LIST (Stored Per Item) ===== */
const taxes = ref([{ name: "", type: "Percentage", value: "" }]);
const addTax = () => taxes.value.push({ name: "", type: "Percentage", value: "" });
const removeTax = (index) => taxes.value.splice(index, 1);

/* ===== VALIDATION ===== */
function validateItem() {
  if (!form.value.item_name.trim()) return "Item name is required";
  if (!form.value.item_price || form.value.item_price <= 0)
    return "Please enter a valid unit price";
  return "";
}

/* ===== SUBMIT ITEM (with taxes array) ===== */
const saveItem = async () => {
  const err = validateItem();
  if (err) {
    toast.warning(err);
    return;
  }

  loading.value = true;
  const userData = JSON.parse(sessionStorage.getItem("ldata")) || {};

  const payload = {
    item_id: form.value.item_id,
    item_name: form.value.item_name,
    item_category: form.value.item_category,
    item_unit: form.value.item_unit,
    item_price: form.value.item_price,
    item_desc: form.value.item_desc,
    item_status: form.value.item_status.toLowerCase(),
    item_discount: form.value.item_discount || 0,
    item_by: userData.user_id || "",
    taxes: taxes.value.filter((t) => t.name && t.value), // ✅ Send full tax list
  };

  try {
    const res = await addItem(payload);

    if (res?.status) {
      toast.success(res.success || "Item saved successfully!");
      setTimeout(() => {
        window.location.href = "/items";
      }, 800);
    } else {
      toast.error(res?.success || "Failed to save item");
    }
  } catch (err) {
    console.error("Save Item Error:", err);
    toast.error("Unexpected error occurred");
  } finally {
    loading.value = false;
  }
};

/* ===== CANCEL / DISCARD ===== */
const cancelItem = () => {
  if (confirm("Discard all changes?")) {
    window.location.href = "/items";
  }
};

/* ===== FETCH ITEM (Edit Mode) ===== */
onMounted(async () => {
  try {
    loading.value = true;
    const urlParams = new URLSearchParams(window.location.search);
    const itemId = urlParams.get("id");

    if (itemId) {
      const res = await getItemById(itemId);

      if (res?.status && res.item_dt) {
        const data = res.item_dt;

        form.value = {
          item_id: data.item_id || "",
          item_name: data.item_name || "",
          item_category: data.item_type || "Product",
          item_unit: data.item_stock_qty || "",
          item_price: data.item_unit_price || "",
          item_desc: data.item_description || "",
          item_status: data.item_status?data.item_status.charAt(0).toUpperCase() + data.item_status.slice(1):"Active" || "Active",
          item_discount: data.item_discount || "",
        };

        // ✅ Load taxes directly from API response
        if (res.taxes && Array.isArray(res.taxes) && res.taxes.length > 0) {
          taxes.value = res.taxes.map((t) => ({
            name: t.item_tax_name,
            type: t.item_tax_type,
            value: t.item_tax_value,
          }));
        } else {
          taxes.value = [{ name: "", type: "Percentage", value: "" }];
        }

        await nextTick();
      }
    }
  } catch (err) {
    console.error("Error loading item:", err);
  } finally {
    loading.value = false;
  }
});
</script>



<style scoped>
.add-item-wrapper {
  background: #fff;
  border-radius: 1rem;
  padding: 2rem 2.5rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
  font-family: "Inter", sans-serif;
  animation: fadeIn 0.6s ease;
}

/* HEADER */
.page-header {
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  margin-bottom: 2.5rem;
  gap: 1rem;
}
.header-left h2 {
  font-size: 1.6rem;
  color: #111827;
  font-weight: 700;
}
.header-left p {
  color: #6b7280;
  font-size: 0.95rem;
}
.header-right {
  display: flex;
  gap: 0.8rem;
}

/* BUTTONS */
.btn-primary,
.btn-secondary {
  border: none;
  padding: 0.8rem 1.6rem;
  border-radius: 10px;
  font-weight: 500;
  cursor: pointer;
  transition: 0.3s;
}
.btn-primary {
  background: #111827;
  color: #fff;
}
.btn-primary:hover {
  background: #1f2937;
}
.btn-secondary {
  background: #f3f4f6;
  color: #111827;
}
.btn-secondary:hover {
  background: #e5e7eb;
}

/* BODY */
.page-body {
  display: flex;
  gap: 2rem;
  align-items: flex-start;
}
.left-pane {
  width: 60%;
}
.right-pane {
  width: 40%;
  overflow: visible;
}

/* CARD */
.card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 1rem;
  padding: 2rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.03);
}

/* FORM GRID */
.form-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem 2rem;
}
.form-group {
  display: flex;
  flex-direction: column;
}
label {
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.4rem;
}
input,
select,
textarea {
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 0.8rem;
  font-size: 0.95rem;
  transition: border 0.3s ease;
}
input:focus,
select:focus,
textarea:focus {
  border-color: #111827;
  outline: none;
}

/* OPTIONS PANEL */
.options-panel {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}
.option-card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 1.4rem;
  transition: all 0.3s ease;
}
.option-card:hover {
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.06);
  transform: translateY(-2px);
}
.option-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #111827;
  margin-bottom: 0.4rem;
}
.subtext {
  color: #6b7280;
  font-size: 0.85rem;
  margin-bottom: 0.8rem;
}

/* TAX CONFIGURATION */
.tax-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}
.tax-box {
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 0.9rem 1rem;
}
.tax-inputs {
  display: flex;
  gap: 0.8rem;
  align-items: center;
  flex-wrap: wrap;
}
.tax-inputs input,
.tax-inputs select {
  flex: 1;
}
.btn-remove {
  background: transparent;
  border: none;
  color: #dc2626;
  cursor: pointer;
  margin-top: 0.6rem;
}
.btn-outline {
  width: 100%;
  border: 1px dashed #9ca3af;
  background: #fff;
  padding: 0.7rem;
  border-radius: 8px;
  color: #111827;
  font-weight: 500;
  cursor: pointer;
}
.btn-outline:hover {
  background: #f3f4f6;
}

/* SWITCH */
.switch-row {
  display: flex;
  align-items: center;
  gap: 0.6rem;
}
.switch {
  position: relative;
  width: 46px;
  height: 24px;
}
.switch input {
  opacity: 0;
}
.slider {
  position: absolute;
  inset: 0;
  background-color: #d1d5db;
  border-radius: 24px;
  transition: 0.4s;
}
.slider:before {
  content: "";
  position: absolute;
  height: 18px;
  width: 18px;
  left: 3px;
  bottom: 3px;
  background-color: white;
  border-radius: 50%;
  transition: 0.4s;
}
input:checked + .slider {
  background-color: #111827;
}
input:checked + .slider:before {
  transform: translateX(22px);
}

/* FOOTER */
.form-footer {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 2rem;
}

/* ANIMATIONS */
.fade-in {
  animation: fadeIn 0.5s ease;
}
.slide-in {
  animation: slideIn 0.5s ease;
}
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateX(8px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@media (max-width: 992px) {
  .page-body {
    flex-direction: column;
  }
  .left-pane,
  .right-pane {
    width: 100%;
  }
}

/* === 📱 MOBILE RESPONSIVE PATCH FOR ADD ITEM PAGE === */
@media (max-width: 992px) {
  .add-item-wrapper {
    padding: 1.5rem !important;
  }

  .page-header {
    flex-direction: column !important;
    align-items: flex-start !important;
    gap: 0.8rem !important;
  }

  .header-right {
    width: 100% !important;
    display: flex !important;
    justify-content: flex-end !important;
    flex-wrap: wrap !important;
    gap: 0.6rem !important;
  }

  .header-right button {
    flex: 1 1 48% !important;
  }

  .page-body {
    flex-direction: column !important;
    gap: 1.5rem !important;
  }

  .left-pane,
  .right-pane {
    width: 100% !important;
  }

  .form-grid {
    grid-template-columns: 1fr !important;
    gap: 1rem !important;
  }

  .form-group input,
  .form-group select,
  .form-group textarea {
    font-size: 0.9rem !important;
    padding: 0.75rem 0.9rem !important;
  }

  .card {
    padding: 1.5rem !important;
  }

  .option-card {
    padding: 1.2rem !important;
  }

  .tax-inputs {
    flex-direction: column !important;
    align-items: stretch !important;
    gap: 0.6rem !important;
  }

  .btn-outline {
    font-size: 0.9rem !important;
  }

  .form-footer {
    flex-direction: column !important;
    align-items: stretch !important;
    gap: 0.8rem !important;
  }

  .form-footer button {
    width: 100% !important;
    font-size: 0.95rem !important;
  }
}

/* === 📱 SMALL SCREENS (≤600px) === */
@media (max-width: 600px) {
  .add-item-wrapper {
    padding: 1rem !important;
    border-radius: 0.8rem !important;
  }

  .header-left h2 {
    font-size: 1.3rem !important;
  }

  .header-left p {
    font-size: 0.85rem !important;
  }

  .header-right {
    flex-direction: column !important;
  }

  .header-right button {
    width: 100% !important;
    font-size: 0.9rem !important;
  }

  .card,
  .option-card {
    padding: 1rem !important;
  }

  .form-grid {
    grid-template-columns: 1fr !important;
  }

  .tax-list {
    gap: 0.8rem !important;
  }

  .tax-box {
    padding: 0.8rem !important;
  }

  .btn-outline {
    padding: 0.6rem !important;
    font-size: 0.85rem !important;
  }

  .form-footer {
    margin-top: 1.5rem !important;
  }

  .form-footer button {
    font-size: 0.9rem !important;
  }
}

/* === 📱 EXTRA SMALL DEVICES (≤400px) === */
@media (max-width: 400px) {
  .header-left h2 {
    font-size: 1.15rem !important;
  }

  .header-right {
    flex-direction: column !important;
    gap: 0.6rem !important;
  }

  .form-grid {
    gap: 0.8rem !important;
  }

  .option-header h4 {
    font-size: 0.95rem !important;
  }

  .subtext {
    font-size: 0.8rem !important;
  }

  .btn-primary,
  .btn-secondary {
    font-size: 0.85rem !important;
    padding: 0.6rem 1rem !important;
  }
}
/* === 🔄 GLOBAL LOADER === */
.global-loader {
  position: fixed;
  inset: 0;
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(3px);
  z-index: 3000;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.loader-spinner {
  width: 48px;
  height: 48px;
  border: 4px solid #d1d5db;
  border-top: 4px solid #111827;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin-bottom: 1rem;
}
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
.global-loader p {
  color: #111827;
  font-weight: 500;
  font-size: 0.95rem;
}

/* === ✅ FORM VALIDATION HIGHLIGHT === */
input.error,
select.error,
textarea.error {
  border-color: #dc2626 !important;
  background-color: #fef2f2 !important;
}
label.error {
  color: #dc2626 !important;
  font-weight: 600;
}

/* === ✨ BUTTON IMPROVEMENTS === */
.btn-primary {
  position: relative;
  transition: all 0.3s ease;
}
.btn-primary:disabled {
  background: #9ca3af !important;
  cursor: not-allowed !important;
  opacity: 0.8;
}
.btn-primary i {
  margin-right: 0.4rem;
}

/* === 🧾 TAX SECTION POLISH === */
.tax-box {
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 0.9rem 1rem;
  transition: all 0.3s ease;
}
.tax-box:hover {
  background: #f3f4f6;
  border-color: #d1d5db;
  transform: translateY(-1px);
}
.tax-inputs input,
.tax-inputs select {
  border-radius: 8px;
  border: 1px solid #d1d5db;
  padding: 0.7rem;
  transition: all 0.3s ease;
}
.tax-inputs input:focus,
.tax-inputs select:focus {
  border-color: #111827;
  box-shadow: 0 0 0 3px rgba(17, 24, 39, 0.1);
}

/* === ⚙️ OPTION PANEL IMPROVEMENTS === */
.option-card {
  transition: all 0.3s ease;
}
.option-card:hover {
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.05);
  transform: translateY(-2px);
}
.option-header i {
  color: #111827;
  font-size: 1.2rem;
}

/* === 📱 RESPONSIVE PATCH === */
@media (max-width: 768px) {
  .page-header {
    flex-direction: column !important;
    align-items: flex-start !important;
    gap: 0.8rem !important;
  }

  .header-right {
    width: 100% !important;
    justify-content: flex-end !important;
    flex-wrap: wrap !important;
    gap: 0.6rem !important;
  }

  .header-right button {
    flex: 1 1 48% !important;
  }

  .form-footer {
    flex-direction: column !important;
    align-items: stretch !important;
    gap: 0.8rem !important;
  }

  .form-footer button {
    width: 100% !important;
  }

  .tax-inputs {
    flex-direction: column !important;
    align-items: stretch !important;
    gap: 0.6rem !important;
  }
}

/* === 🪄 MOBILE UI TWEAKS === */
@media (max-width: 480px) {
  .btn-primary,
  .btn-secondary {
    width: 100% !important;
    text-align: center !important;
  }

  .form-grid {
    grid-template-columns: 1fr !important;
    gap: 1rem !important;
  }

  .tax-box {
    padding: 0.8rem !important;
  }

  .option-card {
    padding: 1rem !important;
  }
}

</style>
